import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// Conta bancária — GET /api/bank-accounts (camelCase pinado ao BankAccountDto).
export interface BankAccount {
  id: number;
  name: string;
  bankAccountNumber: string | null;
  accountType: string | null;
  currencyCode: string;
  currentBalance: number | null;
  balanceUpdatedAt: string | null;
  isBankSync: boolean;
  isArchived: boolean;
  color: string | null;
  companyId: number | null;
  companyName: string | null;
  lastSyncedAt: string | null;
  /** "budgetbakers", ou null quando a conta não vem de fora. */
  externalSource: string | null;
  movementCount: number;
  lastMovementDate: string | null;
}

// Definições da ligação — GET/PUT /api/settings/bank-sync.
export interface BankSyncSettings {
  hasApiToken: boolean;
  enabled: boolean;
  historyStartDate: string | null;
  pollIntervalMinutes: number;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  lastSyncCreatedCount: number;
  lastSyncUpdatedCount: number;
}

export interface SaveBankSyncSettingsPayload {
  apiToken?: string | null;
  clearApiToken?: boolean;
  enabled?: boolean | null;
  historyStartDate?: string | null;
  pollIntervalMinutes?: number | null;
}

export interface BudgetBakersAccountPreview {
  name: string;
  currencyCode: string;
  currentBalance: number | null;
  recordCount: number | null;
  isBankSync: boolean;
  isArchived: boolean;
}

export interface BudgetBakersConnectionTest {
  succeeded: boolean;
  error: string | null;
  accounts: BudgetBakersAccountPreview[];
}

// Resultado de sincronizar — POST /api/bank-movements/sync.
export interface SyncBankMovementsResult {
  succeeded: boolean;
  error: string | null;
  syncedAt: string | null;
  accountsCreated: number;
  accountsUpdated: number;
  movementsCreated: number;
  movementsUpdated: number;
  movementsSkippedVoid: number;
  suppliersMatched: number;
  /** Entradas que ficaram com cliente por o descritivo ser o de uma já associada à mão. */
  clientsMatched: number;
  movementsPendingRecognition: number;
  /** Movimentos que ficaram reconciliados sozinhos, por a fatura ser certa. */
  movementsAutoReconciled: number;
  /** Movimentos sem origem externa no mesmo período, contados só na primeira sincronização. */
  legacyMovementsInWindow: number;
}

/** Uma passagem da sincronização, como a timeline a mostra. */
export interface BankSyncRun {
  id: number;
  startedAt: string;
  /** null quando a sincronização foi interrompida e nunca chegou ao fim. */
  finishedAt: string | null;
  trigger: "manual" | "automatic" | string;
  succeeded: boolean;
  error: string | null;
  accountsCreated: number;
  accountsUpdated: number;
  movementsCreated: number;
  movementsUpdated: number;
  suppliersMatched: number;
  /** Entradas que ficaram com cliente por o descritivo ser o de uma já associada à mão. */
  clientsMatched: number;
  movementsPendingRecognition: number;
  movementsAutoReconciled: number;
  /** Quem carregou no botão. Vazio nas automáticas. */
  triggeredBy: string | null;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function getBankAccounts(): Promise<BankAccount[]> {
  return request<BankAccount[]>("/api/bank-accounts");
}

export function updateBankAccount(
  id: number,
  companyId: number | null,
): Promise<void> {
  return request<void>(`/api/bank-accounts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, companyId }),
  });
}

export function getBankSyncSettings(): Promise<BankSyncSettings> {
  return request<BankSyncSettings>("/api/settings/bank-sync");
}

export function saveBankSyncSettings(
  payload: SaveBankSyncSettingsPayload,
): Promise<BankSyncSettings> {
  return request<BankSyncSettings>("/api/settings/bank-sync", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function testBudgetBakersConnection(
  apiToken?: string | null,
): Promise<BudgetBakersConnectionTest> {
  return request<BudgetBakersConnectionTest>("/api/settings/bank-sync/test", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ apiToken: apiToken ?? null }),
  });
}

export function syncBankMovements(
  fullResync = false,
): Promise<SyncBankMovementsResult> {
  return request<SyncBankMovementsResult>("/api/bank-accounts/sync", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fullResync }),
  });
}

export function getBankSyncRuns(limit = 20): Promise<BankSyncRun[]> {
  return request<BankSyncRun[]>(`/api/bank-accounts/sync-runs?limit=${limit}`);
}
